import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {NgSelectComponent, NgSelectModule} from '@ng-select/ng-select';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {Subject} from 'rxjs';

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, NgSelectModule, FormsModule, TranslateModule],
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiSelectComponent),
    multi: true,
  }]
})
export class MultiSelectComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('ngSelectComponent') ngSelectComponent: NgSelectComponent;
  _innerValue: number[];
  ngClass: Record<string, boolean>;
  private viewInitialized: boolean = false;
  private applySearchTermTimeout: ReturnType<typeof setTimeout> | null = null;

  get innerValue(): number[] {
    return this._innerValue;
  }

  set innerValue(value: number[]) {
    this._innerValue = value;
    this.onChangeCallback(value);
  }

  onChangeCallback: (value: number[]) => void = () => {};

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {}

  writeValue(value: number[]): void {
    if (this.innerValue !== value) {
      this.innerValue = value || [];
    }
  }

  toggle(how: boolean) {
    const sourceItems = this.items || [];
    this.innerValue = how ? sourceItems.map((item) => this.bindValue ? item[this.bindValue] : item) : [];
  }

  closeDropdown(ngSelect: NgSelectComponent) {
    ngSelect.close();
  }

  @Input() items: any[];
  @Input() loading: boolean;
  @Input() disabled: boolean;
  @Input() componentClassName: string = 'multi-select';
  @Input() placeholder: string = 'Search item..';
  @Input() bindLabel: string = 'name';
  @Input() bindValue: string = 'id';
  @Input() appendTo: string;
  @Input() searchTerm: string;
  @Input() virtualScroll: boolean = false;
  @Input() typeahead: Subject<string>;
  @Input() minTermLength: number;

  @Input() groupBy: string;
  @Input() groupValue: (groupKey: string, children: any[]) => any;
  @Input() compareWith: (selected: any, item: any) => boolean;
  @Input() selectableGroup: boolean;
  @Input() selectableGroupAsModel: boolean = true;
  @Input() groupByTemplate: TemplateRef<any>;

  @Input() optionTemplate: TemplateRef<any>;
  @Input() selectedOptionTemplate: TemplateRef<any>;

  // Selecting everything is only safe for a short, fully-loaded list; a paged/searched picker (e.g. banks)
  // wants it gone rather than letting a user "select all" against whatever page happens to be loaded.
  @Input() hideSelectAll: boolean = false;
  // Extra content rendered in the dropdown header next to "clear selection" - e.g. a filter control.
  @Input() headerExtra: TemplateRef<any>;
  // Extra content rendered below the (possibly virtual-scrolled) items list, outside `.ng-dropdown-panel-items`
  // - e.g. static advisory rows that can't share the items' fixed row height that virtualScroll assumes.
  @Input() footerExtra: TemplateRef<any>;

  @Output() search: EventEmitter<{term: string; items: any[]}> = new EventEmitter<{term: string; items: any[]}>();
  @Output() scroll: EventEmitter<{start: number; end: number}> = new EventEmitter<{start: number; end: number}>();
  @Output() scrollToEnd: EventEmitter<void> = new EventEmitter<void>();

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.scheduleApplySearchTerm();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.ngClass = {[this.componentClassName]: true};
    if (!this.viewInitialized) {
      return;
    }

    if (changes['searchTerm']) {
      this.scheduleApplySearchTerm();
    }
  }

  ngOnDestroy() {
    if (this.applySearchTermTimeout) {
      clearTimeout(this.applySearchTermTimeout);
      this.applySearchTermTimeout = null;
    }
  }

  private scheduleApplySearchTerm() {
    if (this.applySearchTermTimeout) {
      clearTimeout(this.applySearchTermTimeout);
      this.applySearchTermTimeout = null;
    }

    this.applySearchTermTimeout = setTimeout(() => this.applySearchTerm(), 300);
  }

  private applySearchTerm() {
    if (
      !this.ngSelectComponent ||
      !this.searchTerm ||
      this.ngSelectComponent.searchTerm === this.searchTerm
    ) {
      return;
    }

    this.ngSelectComponent.filter(this.searchTerm);
  }
}
